import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Twitter } from "lucide-react";

const footerLinks = {
  services: [
    { name: "Software Development", href: "/services" },
    { name: "Cloud Services", href: "/services" },
    { name: "AI & Machine Learning", href: "/services" },
    { name: "Data Engineering", href: "/services" },
    { name: "Digital Transformation", href: "/services" },
  ],
  industries: [
    { name: "Healthcare", href: "/industries" },
    { name: "Financial Services", href: "/industries" },
    { name: "Transportation & Logistics", href: "/industries" },
    { name: "Retail & E-Commerce", href: "/industries" },
    { name: "Manufacturing", href: "/industries" },
  ],
  company: [
    { name: "About Us", href: "/about" },
    { name: "Case Studies", href: "/case-studies" },
    { name: "Careers", href: "/careers" },
    { name: "Blog", href: "/blogs" },
    { name: "Contact", href: "/contact" },
  ],
};

export default function Footer() {
  return (
    <footer 
      data-testid="main-footer"
      className="bg-slate-900 text-white"
    >
      {/* CTA Section */}
      <div className="border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">
                Ready to Transform?
              </p>
              <h2 className="text-4xl sm:text-5xl lg:text-[6vw] font-black tracking-tighter leading-none font-['Outfit']">
                Let's Talk
              </h2>
            </div>
            <Link
              to="/contact"
              data-testid="footer-contact-link"
              className="inline-flex items-center justify-center bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 font-medium transition-colors"
            >
              Start a Conversation
            </Link>
          </div>
        </div>
      </div>

      {/* Links Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <img
              src="https://customer-assets.emergentagent.com/job_tech-solutions-246/artifacts/gucks01m_IMG-5210.jpg"
              alt="Nidus Technologies"
              className="h-12 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-sm">
              Your trusted technology consulting partner. We empower businesses with 
              innovative solutions to thrive in the digital age.
            </p>
            <div className="space-y-3 text-sm text-slate-400">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span>5465 Legacy Drive, Suite 650, Plano, TX 75024 USA</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-blue-400 flex-shrink-0" />
                <a href="tel:+16823317733" className="hover:text-white transition-colors">
                  (682) 331-7733
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-blue-400 flex-shrink-0" />
                <a href="mailto:info@nidustechnologies.com" className="hover:text-white transition-colors">
                  info@nidustechnologies.com
                </a>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-6">Services</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-6">Industries</h3>
            <ul className="space-y-3">
              {footerLinks.industries.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-6">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              &copy; {new Date().getFullYear()} Nidus Technologies. All Rights Reserved.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-white transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
